/***************************************************************************************
 * FileName      : PlaybackTrack.tsx
 * Description   : 플레이백 트랙 컴포넌트 (Phase 구간 + 텍스트 라벨, 제어/센서 구분 섹션, 변경점 마커, 재생 인디케이터)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.13 Phase별 색상 매핑에 실제 시나리오 phaseId 추가 (freq-*, prep, blower-start 등)
 * Reference     : 
 ***************************************************************************************/

import React, { useMemo } from 'react';
// 2026.02.11 : 박은정 : 플레이백 타입 임포트
import { TimelineEvent, ScenarioPhase } from '../types/scenario';
// 2026.02.12 : 박은정 : 시나리오 초기 상태 타입 임포트 (장비 상태 구간 계산용)
import { ScenarioInitialState } from '../types/scenario';

// 2026.02.12 : 박은정 : 상태 ON 구간 타입 정의
interface StateRange {
  startTime: number;
  endTime: number;
}

// 2026.02.12 : 박은정 : 제어 변경점 마커 타입 정의 (ON 구간 내 관련 수치 제어 값 변경 위치)
interface ChangeMarker {
  time: number;
  label: string;
}

// 2026.02.12 : 박은정 : 제어 상태 행 타입 정의
interface StateRow {
  id: string;
  label: string;
  color: string;
  ranges: StateRange[];
  // 2026.02.12 : 박은정 : ON 구간 내 관련 제어 변경점 마커
  changeMarkers: ChangeMarker[];
}

// 2026.02.12 : 박은정 : 센서 그룹 타입 정의 (동일 접두사 센서들의 이벤트 구간)
interface SensorGroup {
  id: string;
  label: string;
  color: string;
  ranges: StateRange[];
}

interface Props {
  totalDuration: number;
  currentTime: number;
  phases: ScenarioPhase[];
  events: TimelineEvent[];
  // 2026.02.12 : 박은정 : 초기 상태 (장비 상태 구간 계산용)
  initialState: ScenarioInitialState;
  onSeek: (time: number) => void;
}

// 2026.02.12 : 박은정 : Phase별 색상 매핑
// 2026.02.13 : 박은정 : 실제 시나리오 phaseId 색상 추가 (freq-*, prep, blower-start, hotline/coldline, normal-trend 등)
export const getPhaseColor = (phaseId: string): string => {
  const colors: Record<string, string> = {
    // 열교환기 공통
    'chiller-start': '#3B82F6',
    'pump-start': '#10B981',
    'heater-start': '#F59E0B',
    'normal-operation': '#8B5CF6',
    'shutdown': '#EF4444',
    // 주파수 응답시험 단계별 색상 (그라데이션 — 낮은 Hz는 연한색, 높은 Hz는 진한색)
    'freq-20hz': '#60A5FA',
    'freq-28hz': '#3B82F6',
    'freq-30hz': '#3B82F6',
    'freq-35hz': '#2563EB',
    'freq-40hz': '#2563EB',
    'freq-45hz': '#1D4ED8',
    'freq-50hz': '#1D4ED8',
    'freq-60hz': '#1E40AF',
    // 순환설비
    'prep': '#94A3B8',
    'blower-start': '#10B981',
    // 열교환기 히터부/칠러부
    'hotline-temp-propagation': '#F59E0B',
    'coldline-temp-propagation': '#06B6D4',
    // 증류설비
    'normal-trend': '#8B5CF6',
  };
  return colors[phaseId] || '#6B7280';
};

// 2026.02.12 : 박은정 : 장비(제어) 상태별 색상 매핑
const getControlColor = (controlId: string): string => {
  const colors: Record<string, string> = {
    'CH-107-RUN': '#3B82F6',
    'PP-102-RUN': '#10B981',
    'PP-106-RUN': '#8B5CF6',
    'EH-108-RUN': '#F59E0B',
    'TCV-101-POS': '#EC4899',
  };
  return colors[controlId] || '#6B7280';
};

// 2026.02.12 : 박은정 : 센서 그룹별 색상 매핑
const getSensorGroupColor = (prefix: string): string => {
  const colors: Record<string, string> = {
    'TIT': '#F59E0B',
    'LIT': '#06B6D4',
    'FQIT': '#10B981',
    'PIT': '#EF4444',
  };
  return colors[prefix] || '#6B7280';
};

// 2026.02.12 : 박은정 : 센서 그룹별 라벨 매핑
const getSensorGroupLabel = (prefix: string): string => {
  const labels: Record<string, string> = {
    'TIT': '온도(TIT)',
    'LIT': '수위(LIT)',
    'FQIT': '유량(FQIT)',
    'PIT': '압력(PIT)',
  };
  return labels[prefix] || prefix;
};

// 2026.02.12 : 박은정 : 제어 ID에서 장비 접두사 추출 (예: "PP-102-RUN" → "PP-102")
const getEquipmentPrefix = (controlId: string): string => {
  const parts = controlId.split('-');
  return parts.length >= 2 ? `${parts[0]}-${parts[1]}` : controlId;
};

// 2026.02.12 : 박은정 : 제어 상태 행 계산 (boolean ON/OFF 구간 + 관련 수치 제어 변경점 마커 + 수치 전용 제어)
const computeStateRows = (
  events: TimelineEvent[],
  initialState: ScenarioInitialState,
  totalDuration: number,
): StateRow[] => {
  const rows: StateRow[] = [];
  const processedPrefixes = new Set<string>();

  // ── boolean 제어: ON/OFF 구간 계산 + 관련 수치 제어 변경점 마커 ──
  const boolControls = Object.entries(initialState.controls)
    .filter(([, v]) => typeof v === 'boolean')
    .map(([id]) => id);

  for (const controlId of boolControls) {
    const initialValue = initialState.controls[controlId] as boolean;
    const controlEvents = events
      .filter((e) => e.id === controlId)
      .sort((a, b) => a.time - b.time);

    // 이벤트가 없는 제어 항목 생략
    if (controlEvents.length === 0) continue;

    // ON 구간 계산
    const ranges: StateRange[] = [];
    let currentValue = initialValue;
    let onStart: number | null = currentValue ? 0 : null;

    for (const event of controlEvents) {
      const newValue = event.value as boolean;
      if (newValue && !currentValue) {
        onStart = event.time;
      } else if (!newValue && currentValue && onStart !== null) {
        ranges.push({ startTime: onStart, endTime: event.time });
        onStart = null;
      }
      currentValue = newValue;
    }
    if (currentValue && onStart !== null) {
      ranges.push({ startTime: onStart, endTime: totalDuration });
    }

    // 2026.02.12 : 박은정 : 관련 수치 제어 변경점 마커 계산 (같은 장비 접두사의 수치 제어 이벤트)
    const prefix = getEquipmentPrefix(controlId);
    processedPrefixes.add(prefix);

    const relatedNumericIds = Object.entries(initialState.controls)
      .filter(([id, v]) => typeof v === 'number' && id !== controlId && getEquipmentPrefix(id) === prefix)
      .map(([id]) => id);

    const changeMarkers: ChangeMarker[] = [];
    for (const relatedId of relatedNumericIds) {
      const relatedEvents = events.filter((e) => e.id === relatedId);
      for (const re of relatedEvents) {
        // ON 구간 내에 있는 이벤트만 마커로 표시
        const inRange = ranges.some((r) => re.time > r.startTime && re.time < r.endTime);
        if (inRange) {
          const shortId = relatedId.replace(prefix + '-', '');
          changeMarkers.push({ time: re.time, label: `${shortId}: ${re.value}` });
        }
      }
    }

    rows.push({
      id: controlId,
      label: prefix,
      color: getControlColor(controlId),
      ranges,
      changeMarkers,
    });
  }

  // ── 수치 전용 제어 (boolean 대응 없는 항목): TCV-101-POS 등 ──
  const numericOnlyControls = Object.entries(initialState.controls)
    .filter(([id, v]) => typeof v === 'number' && !processedPrefixes.has(getEquipmentPrefix(id)))
    .map(([id]) => id);

  // 같은 장비 접두사의 수치 전용 제어를 하나의 행으로 묶음
  const numericPrefixMap = new Map<string, string[]>();
  for (const id of numericOnlyControls) {
    const prefix = getEquipmentPrefix(id);
    if (!numericPrefixMap.has(prefix)) numericPrefixMap.set(prefix, []);
    numericPrefixMap.get(prefix)!.push(id);
  }

  for (const [prefix, ids] of numericPrefixMap.entries()) {
    // 대표 제어 ID 선택 (첫 번째)
    const primaryId = ids[0];
    const initialValue = initialState.controls[primaryId] as number;
    const allEvents = events
      .filter((e) => ids.includes(e.id))
      .sort((a, b) => a.time - b.time);

    if (allEvents.length === 0) continue;

    // value > 0 = "활성" 구간 (대표 제어 기준)
    const primaryEvents = events
      .filter((e) => e.id === primaryId)
      .sort((a, b) => a.time - b.time);

    const ranges: StateRange[] = [];
    let currentValue = initialValue;
    let onStart: number | null = currentValue > 0 ? 0 : null;

    for (const event of primaryEvents) {
      const newValue = event.value as number;
      if (newValue > 0 && currentValue <= 0) {
        onStart = event.time;
      } else if (newValue <= 0 && currentValue > 0 && onStart !== null) {
        ranges.push({ startTime: onStart, endTime: event.time });
        onStart = null;
      }
      currentValue = newValue;
    }
    if (currentValue > 0 && onStart !== null) {
      ranges.push({ startTime: onStart, endTime: totalDuration });
    }

    // 2026.02.12 : 박은정 : 수치 변경점 마커 (활성 구간 내 값 변경)
    const changeMarkers: ChangeMarker[] = [];
    for (const e of allEvents) {
      const inRange = ranges.some((r) => e.time > r.startTime && e.time < r.endTime);
      if (inRange) {
        const shortId = e.id.replace(prefix + '-', '');
        changeMarkers.push({ time: e.time, label: `${shortId}: ${e.value}` });
      }
    }

    rows.push({
      id: primaryId,
      label: prefix,
      color: getControlColor(primaryId),
      ranges,
      changeMarkers,
    });
  }

  return rows;
};

// 2026.02.12 : 박은정 : 센서 그룹별 이벤트 구간 계산 (동일 접두사 센서들의 이벤트를 병합)
const computeSensorGroups = (
  events: TimelineEvent[],
  initialState: ScenarioInitialState,
  totalDuration: number,
): SensorGroup[] => {
  const sensorIds = Object.keys(initialState.sensors);

  // 센서 ID를 접두사(TIT, LIT, FQIT, PIT 등)별로 그룹화
  const prefixMap = new Map<string, string[]>();
  for (const id of sensorIds) {
    const prefix = id.split('-')[0];
    if (!prefixMap.has(prefix)) prefixMap.set(prefix, []);
    prefixMap.get(prefix)!.push(id);
  }

  const groups: SensorGroup[] = [];

  for (const [prefix, ids] of prefixMap.entries()) {
    // 해당 그룹의 센서 이벤트만 추출 및 시간순 정렬
    const groupEvents = events
      .filter((e) => e.type === 'sensor' && ids.includes(e.id))
      .sort((a, b) => a.time - b.time);

    // 이벤트가 없는 센서 그룹 생략
    if (groupEvents.length === 0) continue;

    // 2026.02.12 : 박은정 : 인접 이벤트 병합 (1.5초 이내 간격은 하나의 구간으로 합침)
    const GAP_THRESHOLD = 1.5;
    const ranges: StateRange[] = [];
    let rangeStart = groupEvents[0].time;
    let rangeEnd = groupEvents[0].time;

    for (let i = 1; i < groupEvents.length; i++) {
      if (groupEvents[i].time - rangeEnd <= GAP_THRESHOLD) {
        rangeEnd = groupEvents[i].time;
      } else {
        ranges.push({
          startTime: rangeStart,
          endTime: Math.min(rangeEnd + 0.5, totalDuration),
        });
        rangeStart = groupEvents[i].time;
        rangeEnd = groupEvents[i].time;
      }
    }
    // 마지막 구간 추가
    ranges.push({
      startTime: rangeStart,
      endTime: Math.min(rangeEnd + 0.5, totalDuration),
    });

    groups.push({
      id: prefix,
      label: getSensorGroupLabel(prefix),
      color: getSensorGroupColor(prefix),
      ranges,
    });
  }

  return groups;
};

// 2026.02.12 : 박은정 : 플레이백 트랙 컴포넌트
export const PlaybackTrack: React.FC<Props> = ({
  totalDuration,
  currentTime,
  phases,
  events,
  initialState,
  onSeek,
}) => {
  // 2026.02.12 : 박은정 : 제어 상태 행 계산 (메모이제이션)
  const stateRows = useMemo(
    () => computeStateRows(events, initialState, totalDuration),
    [events, initialState, totalDuration],
  );

  // 2026.02.12 : 박은정 : 센서 그룹 계산 (메모이제이션)
  const sensorGroups = useMemo(
    () => computeSensorGroups(events, initialState, totalDuration),
    [events, initialState, totalDuration],
  );

  // 2026.02.11 : 박은정 : 시간을 퍼센트 위치로 변환
  const timeToPosition = (time: number): number => {
    return totalDuration > 0 ? (time / totalDuration) * 100 : 0;
  };

  // 2026.02.12 : 박은정 : 타임라인 영역 클릭 핸들러 (시크)
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercent = (clickX / rect.width) * 100;
    const seekTime = (clickPercent / 100) * totalDuration;
    onSeek(Math.max(0, Math.min(seekTime, totalDuration)));
  };

  // 2026.02.12 : 박은정 : 현재 재생 위치 (%)
  const currentPosition = timeToPosition(currentTime);

  return (
    <div className="playback-track">
      {/* 2026.02.12 : 박은정 : 레이블 열 (Phase 라벨 + 제어/센서 섹션 헤더 + 각 행 라벨) */}
      <div className="playback-track__labels">
        <div className="playback-track__label playback-track__label--phase">단계</div>

        {/* 2026.02.12 : 박은정 : 제어 섹션 헤더 */}
        <div className="playback-track__section-header playback-track__section-header--control">제어</div>
        {stateRows.map((row) => (
          <div key={row.id} className="playback-track__label playback-track__label--state">
            {row.label}
          </div>
        ))}

        {/* 2026.02.12 : 박은정 : 센서 섹션 헤더 (센서 그룹이 있을 때만 표시) */}
        {sensorGroups.length > 0 && (
          <>
            <div className="playback-track__section-header playback-track__section-header--sensor">센서</div>
            {sensorGroups.map((group) => (
              <div key={group.id} className="playback-track__label playback-track__label--sensor">
                {group.label}
              </div>
            ))}
          </>
        )}
      </div>

      {/* 2026.02.12 : 박은정 : 타임라인 열 (Phase 구간 + 제어/센서 구간 + 인디케이터) */}
      <div className="playback-track__timeline" onClick={handleTimelineClick}>
        {/* Phase 구간 행 */}
        <div className="playback-track__phases-row">
          {phases.map((phase) => {
            const startPos = timeToPosition(phase.startTime);
            const endPos = timeToPosition(phase.endTime);
            const width = endPos - startPos;
            return (
              <div
                key={phase.phaseId}
                className="playback-track__phase"
                style={{
                  left: `${startPos}%`,
                  width: `${width}%`,
                  backgroundColor: getPhaseColor(phase.phaseId),
                }}
                title={phase.name}
              >
                <span className="playback-track__phase-text">{phase.name}</span>
              </div>
            );
          })}
        </div>

        {/* 2026.02.12 : 박은정 : 제어 섹션 구분선 (타임라인 측) */}
        <div className="playback-track__section-divider" />

        {/* 2026.02.12 : 박은정 : 제어 상태 변화 행 (ON/OFF 구간 + 변경점 마커) */}
        {stateRows.map((row) => (
          <div key={row.id} className="playback-track__state-row">
            {/* ON 구간 사각형 */}
            {row.ranges.map((range, ri) => {
              const startPos = timeToPosition(range.startTime);
              const endPos = timeToPosition(range.endTime);
              const width = endPos - startPos;
              return (
                <div
                  key={ri}
                  className="playback-track__state-range"
                  style={{
                    left: `${startPos}%`,
                    width: `${width}%`,
                    backgroundColor: row.color,
                  }}
                  title={`${row.label} ON (${Math.floor(range.startTime)}s ~ ${Math.floor(range.endTime)}s)`}
                >
                  {/* 2026.02.12 : 박은정 : 구간 내 변경점 마커 (값 변경 위치를 흰색 선으로 표시) */}
                  {row.changeMarkers
                    .filter((m) => m.time > range.startTime && m.time < range.endTime)
                    .map((marker, mi) => {
                      const markerPos = ((marker.time - range.startTime) / (range.endTime - range.startTime)) * 100;
                      return (
                        <div
                          key={`cm-${mi}`}
                          className="playback-track__change-marker"
                          style={{ left: `${markerPos}%` }}
                          title={marker.label}
                        />
                      );
                    })}
                </div>
              );
            })}
          </div>
        ))}

        {/* 2026.02.12 : 박은정 : 센서 섹션 구분선 + 센서 그룹 행 (센서 그룹이 있을 때만 표시) */}
        {sensorGroups.length > 0 && (
          <>
            <div className="playback-track__section-divider" />
            {sensorGroups.map((group) => (
              <div key={group.id} className="playback-track__state-row playback-track__sensor-row">
                {group.ranges.map((range, ri) => {
                  const startPos = timeToPosition(range.startTime);
                  const endPos = timeToPosition(range.endTime);
                  const width = endPos - startPos;
                  return (
                    <div
                      key={ri}
                      className="playback-track__state-range playback-track__sensor-range"
                      style={{
                        left: `${startPos}%`,
                        width: `${width}%`,
                        backgroundColor: group.color,
                      }}
                      title={`${group.label} (${Math.floor(range.startTime)}s ~ ${Math.floor(range.endTime)}s)`}
                    />
                  );
                })}
              </div>
            ))}
          </>
        )}

        {/* 2026.02.12 : 박은정 : 재생 인디케이터 (타임라인 전체 높이 관통) */}
        <div
          className="playback-track__indicator"
          style={{ left: `${currentPosition}%` }}
        />
      </div>
    </div>
  );
};
